<!--app\javascript\dashboard\routes\dashboard\bulkmessage\pages\TemplateCreator.vue-->

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import ToastFix from 'dashboard/components-next/toast/ToastFix.vue';
import WhatsAppPreview from '../components/MessagePreview/WhatsAppPreview.vue';
import axios from 'axios';

const store = useStore();

const showAlert = useAlert();
const loading = ref(false);
const nameError = ref('');
const showVariableHelper = ref(false);
const cursorPosition = ref(0);
const bodyTextareaRef = ref(null);
const variableErrors = ref({});

// Inbox management
const selectedInbox = ref(null);
const inboxes = ref([]);
const loadingInboxes = ref(false);
const formattedInboxMetadata = useMapGetter('inboxMetadata/getFormattedInboxMetadata');

// Categories with enhanced information
const categories = [
  {
    value: 'MARKETING',
    name: 'Marketing',
    description: 'Promoções e campanhas',
    icon: 'i-lucide-megaphone',
  },
  {
    value: 'UTILITY',
    name: 'Utilitário',
    description: 'Informações e atualizações',
    icon: 'i-lucide-settings',
  },
  {
    value: 'AUTHENTICATION',
    name: 'Autenticação',
    description: 'Códigos e verificações',
    icon: 'i-lucide-shield-check',
  },
];

// Languages with flags
const languages = [
  { code: 'pt_BR', name: 'Português (BR)', flag: '🇧🇷' },
  { code: 'en_US', name: 'English (US)', flag: '🇺🇸' },
  { code: 'es_ES', name: 'Español', flag: '🇪🇸' },
];

// Template form data
const templateForm = ref({
  name: '',
  category: '',
  language: 'pt_BR',
  header: {
    enabled: false,
    type: 'TEXT',
    text: '',
  },
  body: '',
  footer: {
    enabled: false,
    text: '',
  },
  buttons: [],
  variableExamples: {},
});

// Computed properties
const canSubmit = computed(() => {
  return (
    selectedInbox.value &&
    templateForm.value.name.trim() &&
    templateForm.value.category &&
    templateForm.value.language &&
    templateForm.value.body.trim()
  );
});

const canPreview = computed(() => {
  return templateForm.value.body.trim().length > 0;
});

// Filter only official WhatsApp inboxes (Cloud API)
const whatsappInboxes = computed(() => {
  return inboxes.value.filter(
    inbox =>
      inbox.channel_type === 'whatsapp' &&
      inbox.provider === 'whatsapp_cloud_api' &&
      inbox.integration_type === 'official'
  );
});

// Extract variables from body and create example fields
const templateVariables = computed(() => {
  const body = templateForm.value.body;
  const matches = body.match(/\{\{(\d+)\}\}/g);

  if (!matches) return [];

  const variables = matches
    .map(match => parseInt(match.replace(/\{\{|\}\}/g, '')))
    .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
    .sort((a, b) => a - b); // Sort numerically

  return variables.map(num => ({
    number: num,
    example: templateForm.value.variableExamples?.[num] || '',
  }));
});

// Get next available variable number for auto-completion
const getNextVariableNumber = computed(() => {
  const body = templateForm.value.body;
  const matches = body.match(/\{\{(\d+)\}\}/g);

  if (!matches || matches.length === 0) return 1;

  const numbers = matches
    .map(match => parseInt(match.replace(/\{\{|\}\}/g, '')))
    .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
    .sort((a, b) => a - b); // Sort numerically

  // Find the first missing number in sequence, or next number
  for (let i = 1; i <= numbers.length + 1; i++) {
    if (!numbers.includes(i)) {
      return i;
    }
  }

  return numbers.length + 1;
});

// Input masking functions
const preventInvalidChars = event => {
  const char = event.key;

  // Allow control keys (backspace, delete, tab, enter, arrows, etc.)
  if (
    event.ctrlKey ||
    event.metaKey ||
    [
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
    ].includes(char)
  ) {
    return;
  }

  // Allow spaces (will be converted to underscore in handleTemplateNameInput)
  if (char === ' ') {
    return;
  }

  // Block uppercase letters and special characters (except space which is handled above)
  if (/[A-Z]/.test(char) || (char.length === 1 && !/[a-z0-9_]/.test(char))) {
    event.preventDefault();
    // Show visual feedback
    showInvalidCharFeedback();
  }
};

const handleTemplateNameInput = event => {
  // Clean the input value: replace spaces with underscores, convert uppercase to lowercase, remove other invalid chars
  let cleanValue = event.target.value
    .toLowerCase() // Convert uppercase to lowercase
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/[^a-z0-9_]/g, ''); // Remove any other invalid characters

  templateForm.value.name = cleanValue;

  // Validate after cleaning
  validateTemplateName();
};

const handlePaste = event => {
  event.preventDefault();

  // Get pasted content and clean it
  const clipboardData = event.clipboardData;
  const pastedText = clipboardData ? clipboardData.getData('text') : '';

  // Clean pasted text: convert to lowercase, replace spaces with underscores, remove invalid chars
  const cleanText = pastedText
    .toLowerCase() // Convert uppercase to lowercase
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/[^a-z0-9_]/g, ''); // Remove any other invalid characters

  templateForm.value.name = cleanText;
  validateTemplateName();

  if (cleanText !== pastedText) {
    showInvalidCharFeedback();
  }
};

const showInvalidCharFeedback = () => {
  // Visual feedback for invalid characters
  const input = document.querySelector('input[v-model="templateForm.name"]');
  if (input) {
    input.style.animation = 'shake 0.3s';
    setTimeout(() => {
      input.style.animation = '';
    }, 300);
  }
};

// Template name validation
const validateTemplateName = () => {
  const name = templateForm.value.name;

  if (!name) {
    nameError.value = '';
    return;
  }

  // Check for uppercase letters (shouldn't happen with mask, but double-check)
  if (/[A-Z]/.test(name)) {
    nameError.value = 'Não use letras maiúsculas';
    return;
  }

  // Check for spaces (shouldn't happen with mask, but double-check)
  if (/\s/.test(name)) {
    nameError.value = 'Não use espaços';
    return;
  }

  // Check for special characters (only allow lowercase, numbers, underscore)
  if (!/^[a-z0-9_]*$/.test(name)) {
    nameError.value = 'Apenas letras minúsculas, números e _';
    return;
  }

  // Check minimum length
  if (name.length < 3) {
    nameError.value = 'Mínimo 3 caracteres';
    return;
  }

  // Check if starts with number
  if (/^[0-9]/.test(name)) {
    nameError.value = 'Não pode começar com número';
    return;
  }

  nameError.value = '';
};

// Variable validation
const validateVariableExamples = () => {
  const errors = {};

  templateVariables.value.forEach(variable => {
    const example = templateForm.value.variableExamples?.[variable.number];
    if (!example || !example.trim()) {
      errors[variable.number] = 'Este campo é obrigatório';
    }
  });

  variableErrors.value = errors;
  return Object.keys(errors).length === 0;
};

// Clear variable errors when user types
const clearVariableError = variableNumber => {
  if (variableErrors.value[variableNumber]) {
    delete variableErrors.value[variableNumber];
    variableErrors.value = { ...variableErrors.value };
  }
};

// Variable auto-completion and smart deletion functions
const handleBodyInput = event => {
  const textarea = event.target;
  const value = textarea.value;
  const cursorPos = textarea.selectionStart;

  cursorPosition.value = cursorPos;

  // Check if user typed {{ at cursor position
  const beforeCursor = value.substring(0, cursorPos);
  const autoCompleteMatch = beforeCursor.match(/\{\{$/);

  if (autoCompleteMatch) {
    // Auto-complete with next variable number immediately
    const nextNumber = getNextVariableNumber.value;
    const afterCursor = value.substring(cursorPos);

    // Replace {{ with {{nextNumber}}
    const newValue =
      beforeCursor.replace(/\{\{$/, `{{${nextNumber}}}`) + afterCursor;
    templateForm.value.body = newValue;

    // Set cursor position after the inserted variable
    setTimeout(() => {
      const newCursorPos = cursorPos + nextNumber.toString().length + 2; // +2 for }}
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 10);
    return;
  }

  // Check for incomplete variables and clean them up
  cleanupIncompleteVariables();
};

// Clean up incomplete variables like {{1} (missing closing brace)
const cleanupIncompleteVariables = () => {
  const value = templateForm.value.body;

  // Find and remove incomplete variables ({{number} without closing brace)
  const cleanedValue = value.replace(/\{\{(\d+)\}(?!\})/g, '');

  if (cleanedValue !== value) {
    templateForm.value.body = cleanedValue;

    // Restore cursor position
    setTimeout(() => {
      if (bodyTextareaRef.value) {
        const textarea = bodyTextareaRef.value;
        const newCursorPos = Math.min(
          cursorPosition.value,
          cleanedValue.length
        );
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();
      }
    }, 10);
  }
};

const addNextVariable = () => {
  const nextNumber = getNextVariableNumber.value;

  // Insert the next variable at cursor position or end
  const textarea = bodyTextareaRef.value;
  if (textarea) {
    const cursorPos = textarea.selectionStart;
    const value = templateForm.value.body;
    const beforeCursor = value.substring(0, cursorPos);
    const afterCursor = value.substring(cursorPos);

    const newVariable = `{{${nextNumber}}}`;
    templateForm.value.body = beforeCursor + newVariable + afterCursor;

    // Set cursor position after the inserted variable
    setTimeout(() => {
      const newCursorPos = cursorPos + newVariable.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 10);
  } else {
    // If no textarea reference, just append to end
    templateForm.value.body += ` {{${nextNumber}}}`;
  }
};

// Helper functions for preview
const getCategoryName = categoryValue => {
  const category = categories.find(c => c.value === categoryValue);
  return category ? category.name : categoryValue;
};

const getLanguageName = languageCode => {
  const language = languages.find(l => l.code === languageCode);
  return language ? language.name : languageCode;
};

// Format message for WhatsApp preview
const formatPreviewMessage = computed(() => {
  let message = '';

  // Add header
  if (templateForm.value.header.enabled && templateForm.value.header.text) {
    message += `**${templateForm.value.header.text}**\n\n`;
  }

  // Add body with variable examples or placeholders
  if (templateForm.value.body) {
    let body = templateForm.value.body;
    // Replace variables with examples or default placeholders
    body = body.replace(/\{\{(\d+)\}\}/g, (_, num) => {
      const example = templateForm.value.variableExamples?.[num];
      return example || `{{${num}}}`;
    });
    message += body;
  }

  // Add footer
  if (templateForm.value.footer.enabled && templateForm.value.footer.text) {
    message += `\n\n_${templateForm.value.footer.text}_`;
  }

  return message;
});

// Format buttons for WhatsApp preview
const formatPreviewButtons = computed(() => {
  return templateForm.value.buttons
    .filter(btn => btn.text && btn.text.trim())
    .map(btn => ({
      type: btn.type,
      text: btn.text.trim(),
      url: btn.url || undefined,
      phone_number: btn.phone_number || undefined,
    }));
});

// Authentication helpers
const getCookie = name => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };
  } catch (e) {
    console.error('Erro ao ler o cookie:', e);
    return {};
  }
};

// Button management
const addButton = () => {
  if (templateForm.value.buttons.length < 3) {
    templateForm.value.buttons.push({
      type: 'QUICK_REPLY',
      text: '',
      url: '',
      phone_number: '',
    });
  }
};

const removeButton = index => {
  templateForm.value.buttons.splice(index, 1);
};

// Load inboxes
const loadInboxes = async () => {
  loadingInboxes.value = true;
  try {
    const inboxMetadata = formattedInboxMetadata.value;

    if (!inboxMetadata || !inboxMetadata.results || !Array.isArray(inboxMetadata.results)) {
      console.error('Invalid store data structure:', inboxMetadata);
      inboxes.value = [];
      return;
    }

    const data = inboxMetadata;
    const inboxMap = new Map();

    data.results.forEach(group => {
      if (!group) return;

      // Only process official WhatsApp API inboxes
      if (group.integration_type === 'official') {
        const displayName = store.getters['inboxMetadata/getInboxDisplayName'](group.inbox_id);
        const inboxData = {
          id: group.inbox_id || 0,
          name: displayName || group.inbox_name || `WhatsApp ${group.display_phone_number || ''}`,
          inbox_name: displayName || group.inbox_name || `WhatsApp ${group.display_phone_number || ''}`,
          display_name: displayName,
          display_phone_number: group.display_phone_number,
          channel_type: 'whatsapp',
          provider: 'whatsapp_cloud_api',
          integration_type: 'official',
          isConnected: true,
        };
        inboxMap.set(group.inbox_id, inboxData);
      }
    });

    inboxes.value = Array.from(inboxMap.values());

    // Auto-select first inbox if available
    if (inboxes.value.length > 0 && !selectedInbox.value) {
      selectedInbox.value = inboxes.value[0].id;
    }
  } catch (err) {
    console.error('Failed to load inboxes:', err);
    inboxes.value = [];
  } finally {
    loadingInboxes.value = false;
  }
};

// Initialize on mount
onMounted(() => {
  loadInboxes();
});

// Form submission
const submitTemplate = async () => {
  if (!canSubmit.value || nameError.value) return;

  // Validate inbox selection
  if (!selectedInbox.value) {
    showAlert('Por favor, selecione um inbox do WhatsApp oficial', 'error');
    return;
  }

  // Validate template name one more time
  validateTemplateName();
  if (nameError.value) {
    showAlert(nameError.value, 'error');
    return;
  }

  // Validate variable examples if there are variables
  if (templateVariables.value.length > 0) {
    const isValidVariables = validateVariableExamples();
    if (!isValidVariables) {
      showAlert(
        'Preencha todos os exemplos das variáveis obrigatórios',
        'error'
      );
      return;
    }
  }

  loading.value = true;

  try {
    // Prepare template data
    const templateData = {
      name: templateForm.value.name, // Use the validated name as-is
      category: templateForm.value.category,
      language: templateForm.value.language,
      components: [],
    };

    // Add header component
    if (templateForm.value.header.enabled && templateForm.value.header.text) {
      templateData.components.push({
        type: 'HEADER',
        format: templateForm.value.header.type,
        text: templateForm.value.header.text,
      });
    }

    // Add body component
    templateData.components.push({
      type: 'BODY',
      text: templateForm.value.body,
    });

    // Add footer component
    if (templateForm.value.footer.enabled && templateForm.value.footer.text) {
      templateData.components.push({
        type: 'FOOTER',
        text: templateForm.value.footer.text,
      });
    }

    // Add buttons component
    if (templateForm.value.buttons.length > 0) {
      const buttons = templateForm.value.buttons
        .filter(btn => btn.text.trim())
        .map(btn => {
          const button = {
            type: btn.type,
            text: btn.text.trim(),
          };

          if (btn.type === 'URL' && btn.url) {
            button.url = btn.url;
          } else if (btn.type === 'PHONE_NUMBER' && btn.phone_number) {
            button.phone_number = btn.phone_number;
          }

          return button;
        });

      if (buttons.length > 0) {
        templateData.components.push({
          type: 'BUTTONS',
          buttons: buttons,
        });
      }
    }

    // Add variableExamples to the template data
    if (Object.keys(templateForm.value.variableExamples).length > 0) {
      templateData.variableExamples = templateForm.value.variableExamples;
    }

    // Submit to API with inbox_id in URL
    const response = await axios.post(
      `/api/v1/whatsapp/${selectedInbox.value}/templates`,
      {
        template: templateData,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute('content') || '',
          ...getAuthHeaders(),
        },
        withCredentials: true,
      }
    );

    if (response.data && response.data.template) {
      showAlert(
        'Template criado com sucesso! Aguarde a aprovação do WhatsApp (até 24h).',
        'success'
      );
      resetForm();
    } else {
      throw new Error('Resposta inválida do servidor');
    }
  } catch (error) {
    console.error('Erro ao criar template:', error);
    const errorMessage =
      error.response?.data?.error || error.message || 'Erro ao criar template';
    showAlert(errorMessage, 'error');
  } finally {
    loading.value = false;
  }
};

// Reset form
const resetForm = () => {
  templateForm.value = {
    name: '',
    category: '',
    language: 'pt_BR',
    header: {
      enabled: false,
      type: 'TEXT',
      text: '',
    },
    body: '',
    footer: {
      enabled: false,
      text: '',
    },
    buttons: [],
    variableExamples: {},
  };

  // Clear all errors
  nameError.value = '';
  variableErrors.value = {};

  // Reset inbox to first available
  if (whatsappInboxes.value.length > 0) {
    selectedInbox.value = whatsappInboxes.value[0].id;
  }
};
</script>

<template>
  <div
    class="flex flex-1 h-full justify-between flex-col m-0 bg-n-background dark:bg-slate-900 overflow-auto"
  >
    <!-- Enhanced Header -->
    <div
      class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-b border-blue-100 dark:border-blue-800/30"
    >
      <div class="flex justify-between items-center h-16 px-6">
        <div class="flex items-center gap-4">
          <div>
            <h1 class="text-2xl font-bold text-n-slate-12 mb-1">
              <woot-sidemenu-icon v-if="showSidemenuIcon" />
              Criar Template WhatsApp
            </h1>
          </div>
        </div>
        <div class="flex items-center gap-2 text-sm text-n-slate-11">
          <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          Templates são aprovados em até 24h
        </div>
      </div>
    </div>

    <!-- Toast Fix Component -->
    <ToastFix />

    <!-- Main Content Area -->
    <div class="template-creator-container dark:bg-slate-900 relative flex-1">
      <!-- Loading Modal -->
      <div
        v-if="loading"
        class="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/75"
      >
        <div
          class="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-md w-full shadow-xl border border-n-slate-3 dark:border-slate-700"
        >
          <div class="flex items-center justify-center mb-4">
            <div
              class="w-16 h-16 bg-n-blue-1 dark:bg-blue-900/30 rounded-full flex items-center justify-center"
            >
              <div
                class="animate-spin w-8 h-8 border-3 border-n-blue-11 border-t-transparent rounded-full"
              />
            </div>
          </div>
          <h2
            class="text-xl font-semibold text-center mb-4 text-slate-900 dark:text-slate-100"
          >
            Criando Template
          </h2>
          <p class="text-slate-700 dark:text-slate-300 mb-4 text-center">
            Aguarde enquanto criamos seu template...
          </p>
        </div>
      </div>

      <!-- Enhanced Split Layout -->
      <div class="flex gap-6 max-w-7xl mx-auto p-6">
        <!-- Left Column: Form -->
        <div class="flex-1 max-w-2xl">
          <!-- Enhanced Info Notice -->
          <div
            class="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 mb-6 shadow-sm"
          >
            <div class="flex items-start">
              <div
                class="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center mr-4"
              >
                <i class="i-lucide-info text-white text-lg" />
              </div>
              <div>
                <h4 class="font-semibold text-n-slate-12 mb-2">
                  Sobre Templates WhatsApp
                </h4>
                <p class="text-sm text-n-slate-11 leading-relaxed">
                  Templates são mensagens pré-aprovadas pelo WhatsApp que você
                  pode usar para iniciar conversas com clientes. Após a criação,
                  podem levar até 24 horas para serem aprovados pelo WhatsApp.
                </p>
              </div>
            </div>
          </div>

          <!-- Inbox Selection -->
          <div
            class="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-n-slate-3 dark:border-slate-700 mb-6"
          >
            <h3
              class="text-lg font-semibold text-n-slate-12 dark:text-slate-200 mb-4 flex items-center gap-2"
            >
              <i class="i-lucide-inbox text-n-brand" />
              Selecione o Inbox WhatsApp Oficial
            </h3>

            <div v-if="loadingInboxes" class="text-center py-4">
              <div
                class="animate-spin w-6 h-6 border-2 border-n-brand border-t-transparent rounded-full mx-auto"
              />
              <p class="text-sm text-n-slate-11 mt-2">Carregando inboxes...</p>
            </div>

            <div v-else-if="whatsappInboxes.length === 0" class="text-center py-4">
              <div
                class="w-12 h-12 bg-n-amber-2 dark:bg-n-amber-3 rounded-full flex items-center justify-center mx-auto mb-3"
              >
                <i class="i-lucide-alert-triangle text-n-amber-11 text-xl" />
              </div>
              <p class="text-n-slate-11 dark:text-slate-400 mb-2">
                Nenhum inbox do WhatsApp oficial encontrado
              </p>
              <p class="text-xs text-n-slate-10">
                Configure um inbox do WhatsApp Cloud API para criar templates
              </p>
            </div>

            <div v-else class="grid grid-cols-1 gap-3">
              <button
                v-for="inbox in whatsappInboxes"
                :key="inbox.id"
                type="button"
                class="p-4 border-2 rounded-lg transition-all duration-200 text-left"
                :class="{
                  'border-n-brand bg-n-brand/5 text-n-brand':
                    selectedInbox === inbox.id,
                  'border-n-slate-3 dark:border-slate-600 hover:border-n-brand/50':
                    selectedInbox !== inbox.id,
                }"
                @click="selectedInbox = inbox.id"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-10 h-10 rounded-lg flex items-center justify-center"
                    :class="{
                      'bg-n-brand text-white': selectedInbox === inbox.id,
                      'bg-grass-300 text-grass-800 dark:bg-grass-200':
                        selectedInbox !== inbox.id,
                    }"
                  >
                    <i class="i-lucide-message-circle" />
                  </div>
                  <div class="flex-1">
                    <div class="font-medium">
                      {{ inbox.display_name || inbox.name || inbox.inbox_name }}
                    </div>
                    <div
                      v-if="inbox.display_phone_number"
                      class="text-xs text-n-slate-11"
                    >
                      {{ inbox.display_phone_number }}
                    </div>
                  </div>
                  <div
                    v-if="selectedInbox === inbox.id"
                    class="w-6 h-6 bg-n-brand rounded-full flex items-center justify-center"
                  >
                    <i class="i-lucide-check text-white text-sm" />
                  </div>
                </div>
              </button>
            </div>
          </div>

          <!-- Enhanced Template Form -->
          <div
            class="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-n-slate-3 dark:border-slate-700"
          >
            <form @submit.prevent="submitTemplate">
              <!-- Template Name with Enhanced Validation -->
              <div class="mb-6">
                <label
                  class="block text-sm font-semibold text-n-slate-12 dark:text-slate-200 mb-2"
                >
                  <i class="i-lucide-type mr-2 text-n-brand" />
                  Nome do Template *
                </label>
                <div class="relative">
                  <input
                    v-model="templateForm.name"
                    type="text"
                    required
                    class="w-full px-4 py-3 border rounded-lg bg-white dark:bg-slate-700 text-n-slate-12 dark:text-slate-200 focus:outline-none focus:ring-2 transition-all duration-200"
                    :class="{
                      'border-red-300 focus:ring-red-500/20 focus:border-red-500':
                        nameError,
                      'border-green-300 focus:ring-green-500/20 focus:border-green-500':
                        templateForm.name && !nameError,
                      'border-n-slate-3 dark:border-slate-600 focus:ring-n-brand/20 focus:border-n-brand':
                        !templateForm.name,
                    }"
                    placeholder="ex: boas_vindas_cliente"
                    autocomplete="off"
                    spellcheck="false"
                    @input="handleTemplateNameInput"
                    @keydown="preventInvalidChars"
                    @paste="handlePaste"
                  />
                  <div
                    class="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <i
                      v-if="nameError"
                      class="i-lucide-x-circle text-red-500"
                    />
                    <i
                      v-else-if="templateForm.name && !nameError"
                      class="i-lucide-check-circle text-green-500"
                    />
                  </div>
                </div>
                <div class="mt-2 space-y-1">
                  <p
                    v-if="nameError"
                    class="text-xs text-red-600 dark:text-red-400 flex items-center gap-1"
                  >
                    <i class="i-lucide-alert-circle" />
                    {{ nameError }}
                  </p>
                  <p
                    class="text-xs text-n-slate-11 dark:text-slate-400 flex items-center gap-1"
                  >
                    <i class="i-lucide-info" />
                    Apenas letras minúsculas, números e underscores. Sem espaços
                    ou caracteres especiais.
                  </p>
                  <p
                    v-if="templateForm.name && !nameError"
                    class="text-xs text-green-600 dark:text-green-400 flex items-center gap-1"
                  >
                    <i class="i-lucide-check" />
                    Nome válido!
                  </p>
                </div>
              </div>

              <!-- Enhanced Category Selection -->
              <div class="mb-6">
                <label
                  class="block text-sm font-semibold text-n-slate-12 dark:text-slate-200 mb-2"
                >
                  <i class="i-lucide-tag mr-2 text-n-brand" />
                  Categoria *
                </label>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <button
                    v-for="category in categories"
                    :key="category.value"
                    type="button"
                    class="p-4 border-2 rounded-lg transition-all duration-200 text-left"
                    :class="{
                      'border-n-brand bg-n-brand/5 text-n-brand':
                        templateForm.category === category.value,
                      'border-n-slate-3 dark:border-slate-600 hover:border-n-brand/50':
                        templateForm.category !== category.value,
                    }"
                    @click="templateForm.category = category.value"
                  >
                    <div class="flex items-center gap-2 mb-1">
                      <i :class="category.icon" class="text-lg" />
                      <span class="font-medium">{{ category.name }}</span>
                    </div>
                    <p class="text-xs text-n-slate-11">
                      {{ category.description }}
                    </p>
                  </button>
                </div>
              </div>

              <!-- Enhanced Language Selection -->
              <div class="mb-6">
                <label
                  class="block text-sm font-semibold text-n-slate-12 dark:text-slate-200 mb-2"
                >
                  <i class="i-lucide-globe mr-2 text-n-brand" />
                  Idioma *
                </label>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <button
                    v-for="language in languages"
                    :key="language.code"
                    type="button"
                    class="p-3 border-2 rounded-lg transition-all duration-200 text-left"
                    :class="{
                      'border-n-brand bg-n-brand/5 text-n-brand':
                        templateForm.language === language.code,
                      'border-n-slate-3 dark:border-slate-600 hover:border-n-brand/50':
                        templateForm.language !== language.code,
                    }"
                    @click="templateForm.language = language.code"
                  >
                    <div class="flex items-center gap-2">
                      <span class="text-lg">{{ language.flag }}</span>
                      <span class="font-medium text-sm">{{
                        language.name
                      }}</span>
                    </div>
                  </button>
                </div>
              </div>

              <!-- Header Section -->
              <div class="mb-6">
                <div class="flex items-center justify-between mb-3">
                  <label
                    class="block text-sm font-medium text-n-slate-12 dark:text-slate-200"
                  >
                    Cabeçalho (Opcional)
                  </label>
                  <button
                    type="button"
                    class="text-sm text-n-brand hover:text-n-brand/80"
                    @click="
                      templateForm.header.enabled = !templateForm.header.enabled
                    "
                  >
                    {{ templateForm.header.enabled ? 'Remover' : 'Adicionar' }}
                    Cabeçalho
                  </button>
                </div>

                <div v-if="templateForm.header.enabled" class="space-y-3">
                  <select
                    v-model="templateForm.header.type"
                    class="w-full px-3 py-2 border border-n-slate-3 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-n-slate-12 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-n-brand"
                  >
                    <option value="TEXT">Texto</option>
                    <option value="IMAGE">Imagem</option>
                    <option value="VIDEO">Vídeo</option>
                    <option value="DOCUMENT">Documento</option>
                  </select>

                  <input
                    v-if="templateForm.header.type === 'TEXT'"
                    v-model="templateForm.header.text"
                    type="text"
                    maxlength="60"
                    class="w-full px-3 py-2 border border-n-slate-3 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-n-slate-12 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-n-brand"
                    placeholder="Texto do cabeçalho (máx. 60 caracteres)"
                  />
                </div>
              </div>

              <!-- Enhanced Body Section with Auto-completion -->
              <div class="mb-6">
                <div class="flex items-center justify-between mb-2">
                  <label
                    class="block text-sm font-semibold text-n-slate-12 dark:text-slate-200"
                  >
                    <i class="i-lucide-message-square mr-2 text-n-brand" />
                    Corpo da Mensagem *
                  </label>
                  <button
                    type="button"
                    class="px-3 py-1.5 bg-n-brand text-white rounded-lg hover:brightness-110 text-xs flex items-center gap-1 transition-all duration-200"
                    title="Adicionar próxima variável"
                    @click="addNextVariable"
                  >
                    <i class="i-lucide-plus" />
                    Adicionar Variável
                  </button>
                </div>
                <div class="relative">
                  <textarea
                    ref="bodyTextareaRef"
                    v-model="templateForm.body"
                    required
                    rows="10"
                    maxlength="1024"
                    class="w-full px-4 py-3 border border-n-slate-3 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-n-slate-12 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand resize-y transition-all duration-200 min-h-[200px]"
                    placeholder="Digite o conteúdo da sua mensagem aqui."
                    @input="handleBodyInput"
                  />
                </div>
                <div class="flex justify-between items-center text-xs mt-2">
                  <div class="flex items-center gap-4">
                    <!-- Template Variables Helper -->
                    <div class="relative">
                      <button
                        type="button"
                        class="text-n-brand hover:text-n-brand/80 flex items-center gap-1 text-xs"
                        @click="showVariableHelper = !showVariableHelper"
                      >
                        <i class="i-lucide-help-circle" />
                        Ver variáveis
                      </button>
                      <div
                        v-if="showVariableHelper"
                        class="absolute top-full left-0 mt-1 z-20 w-72 bg-white dark:bg-slate-800 border border-n-slate-3 rounded-lg shadow-lg p-3"
                      >
                        <h5 class="font-medium text-sm mb-2">
                          Variáveis disponíveis:
                        </h5>
                        <div class="space-y-1 text-xs">
                          <div class="flex justify-between">
                            <code class="text-n-brand"
                              >&#123;&#123;1&#125;&#125;</code
                            >
                            <span class="text-n-slate-11"
                              >Primeira variável</span
                            >
                          </div>
                          <div class="flex justify-between">
                            <code class="text-n-brand"
                              >&#123;&#123;2&#125;&#125;</code
                            >
                            <span class="text-n-slate-11"
                              >Segunda variável</span
                            >
                          </div>
                          <div class="flex justify-between">
                            <code class="text-n-brand"
                              >&#123;&#123;3&#125;&#125;</code
                            >
                            <span class="text-n-slate-11"
                              >Terceira variável</span
                            >
                          </div>
                        </div>
                        <div class="border-t border-n-slate-3 mt-2 pt-2">
                          <div class="text-xs text-n-slate-11">
                            💡 Dica: Use o botão "Adicionar Variável" para
                            inserir automaticamente a próxima variável em
                            sequência
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <span
                    class="font-mono text-n-slate-11 dark:text-slate-400"
                    :class="{
                      'text-red-500': templateForm.body.length > 900,
                      'text-amber-500': templateForm.body.length > 800,
                    }"
                  >
                    {{ templateForm.body.length }}/1024
                  </span>
                </div>
              </div>

              <!-- Variable Examples Section -->
              <div v-if="templateVariables.length > 0" class="mb-6">
                <div class="flex items-center gap-2 mb-4">
                  <i class="i-lucide-edit-3 text-n-brand" />
                  <h4
                    class="text-sm font-semibold text-n-slate-12 dark:text-slate-200"
                  >
                    Exemplos das Variáveis
                  </h4>
                  <span
                    class="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full"
                  >
                    {{ templateVariables.length }} variáve{{
                      templateVariables.length > 1 ? 'is' : 'l'
                    }}
                  </span>
                </div>
                <div class="space-y-3">
                  <div
                    v-for="variable in templateVariables"
                    :key="variable.number"
                    class="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
                  >
                    <!-- Parameter index badge -->
                    <div
                      class="flex-shrink-0 w-7 h-7 bg-n-brand rounded-lg flex items-center justify-center"
                    >
                      <span class="text-xs font-semibold text-white">{{
                        variable.number
                      }}</span>
                    </div>

                    <!-- Input field -->
                    <div class="flex-1">
                      <div class="flex items-center gap-2">
                        <input
                          v-model="
                            templateForm.variableExamples[variable.number]
                          "
                          type="text"
                          class="flex-1 px-3 py-2 text-sm bg-white dark:bg-n-slate-1 border rounded-lg placeholder:text-n-slate-10 text-n-slate-12 focus:outline-none focus:ring-2 transition-all duration-200 hover:border-n-slate-4 reset-base"
                          :class="{
                            'border-red-300 focus:ring-red-500/20 focus:border-red-500':
                              variableErrors[variable.number],
                            'border-n-slate-3 focus:ring-n-brand/20 focus:border-n-brand':
                              !variableErrors[variable.number],
                          }"
                          :placeholder="`Exemplo para variável ${variable.number}`"
                          @input="clearVariableError(variable.number)"
                        />
                      </div>
                      <!-- Error message -->
                      <div v-if="variableErrors[variable.number]" class="mt-1">
                        <p
                          class="text-xs text-red-600 dark:text-red-400 flex items-center gap-1"
                        >
                          <i class="i-lucide-alert-circle" />
                          {{ variableErrors[variable.number] }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg"
                >
                  <div class="flex items-start gap-2">
                    <i
                      class="i-lucide-lightbulb text-amber-600 dark:text-amber-400 text-sm mt-0.5"
                    />
                    <div class="text-xs text-amber-700 dark:text-amber-300">
                      <strong>Dica:</strong> Os exemplos ajudam a entender como
                      as variáveis serão usadas e aparecem na prévia do
                      template. São enviados para o WhatsApp como referência
                      durante a aprovação.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Footer Section -->
              <div class="mb-6">
                <div class="flex items-center justify-between mb-3">
                  <label
                    class="block text-sm font-medium text-n-slate-12 dark:text-slate-200"
                  >
                    Rodapé (Opcional)
                  </label>
                  <button
                    type="button"
                    class="text-sm text-n-brand hover:text-n-brand/80"
                    @click="
                      templateForm.footer.enabled = !templateForm.footer.enabled
                    "
                  >
                    {{ templateForm.footer.enabled ? 'Remover' : 'Adicionar' }}
                    Rodapé
                  </button>
                </div>

                <input
                  v-if="templateForm.footer.enabled"
                  v-model="templateForm.footer.text"
                  type="text"
                  maxlength="60"
                  class="w-full px-3 py-2 border border-n-slate-3 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-n-slate-12 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-n-brand"
                  placeholder="Texto do rodapé (máx. 60 caracteres)"
                />
              </div>

              <!-- Buttons Section -->
              <div class="mb-6">
                <div class="flex items-center justify-between mb-3">
                  <label
                    class="block text-sm font-medium text-n-slate-12 dark:text-slate-200"
                  >
                    Botões (Opcional)
                  </label>
                  <button
                    type="button"
                    :disabled="templateForm.buttons.length >= 3"
                    class="text-sm text-n-brand hover:text-n-brand/80 disabled:opacity-50 disabled:cursor-not-allowed"
                    @click="addButton"
                  >
                    Adicionar Botão
                  </button>
                </div>

                <div v-if="templateForm.buttons.length > 0" class="space-y-3">
                  <div
                    v-for="(button, index) in templateForm.buttons"
                    :key="index"
                    class="flex gap-3 items-start p-3 border border-n-slate-3 dark:border-slate-600 rounded-lg"
                  >
                    <div class="flex-1 space-y-2">
                      <select
                        v-model="button.type"
                        class="w-full px-3 py-2 border border-n-slate-3 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-n-slate-12 dark:text-slate-200 text-sm"
                      >
                        <option value="QUICK_REPLY">Resposta Rápida</option>
                        <option value="URL">URL</option>
                        <option value="PHONE_NUMBER">Telefone</option>
                      </select>

                      <input
                        v-model="button.text"
                        type="text"
                        maxlength="25"
                        class="w-full px-3 py-2 border border-n-slate-3 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-n-slate-12 dark:text-slate-200 text-sm"
                        placeholder="Texto do botão (máx. 25 caracteres)"
                      />

                      <input
                        v-if="button.type === 'URL'"
                        v-model="button.url"
                        type="url"
                        class="w-full px-3 py-2 border border-n-slate-3 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-n-slate-12 dark:text-slate-200 text-sm"
                        placeholder="https://exemplo.com"
                      />

                      <input
                        v-if="button.type === 'PHONE_NUMBER'"
                        v-model="button.phone_number"
                        type="tel"
                        class="w-full px-3 py-2 border border-n-slate-3 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-n-slate-12 dark:text-slate-200 text-sm"
                        placeholder="+5511999999999"
                      />
                    </div>

                    <button
                      type="button"
                      class="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      @click="removeButton(index)"
                    >
                      <i class="i-lucide-trash-2 text-sm" />
                    </button>
                  </div>
                </div>
              </div>

              <!-- Submit Buttons -->
              <div
                class="flex justify-between pt-4 border-t border-n-slate-3 dark:border-slate-600"
              >
                <button
                  type="button"
                  class="px-4 py-2 text-n-slate-11 dark:text-slate-400 bg-n-slate-2 dark:bg-slate-700 hover:bg-n-slate-3 dark:hover:bg-slate-600 rounded-lg border border-n-slate-3 dark:border-slate-600 transition-colors"
                  @click="resetForm"
                >
                  Limpar Formulário
                </button>

                <button
                  type="submit"
                  :disabled="!canSubmit || loading"
                  class="px-6 py-2 bg-n-brand text-white rounded-lg hover:brightness-110 focus:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <span v-if="loading" class="flex items-center gap-2">
                    <i class="i-lucide-loader animate-spin" />
                    Criando...
                  </span>
                  <span v-else>Criar Template</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Right Column: Enhanced WhatsApp Preview -->
        <div class="w-96 flex-shrink-0">
          <div class="sticky top-6">
            <div
              class="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-n-slate-3 dark:border-slate-700"
            >
              <div class="flex items-center gap-2 mb-4">
                <i class="i-lucide-smartphone text-lg text-n-brand" />
                <h3
                  class="text-lg font-medium text-n-slate-12 dark:text-slate-200"
                >
                  Prévia WhatsApp
                </h3>
              </div>

              <!-- Template Info -->
              <div
                v-if="templateForm.name || templateForm.category"
                class="mb-4 p-3 bg-n-slate-1 dark:bg-n-slate-2 rounded-lg"
              >
                <div
                  v-if="templateForm.name"
                  class="text-sm font-medium text-n-slate-12 mb-1"
                >
                  {{ templateForm.name }}
                </div>
                <div class="text-xs text-n-slate-11 flex items-center gap-2">
                  <span
                    v-if="templateForm.category"
                    class="inline-flex items-center"
                  >
                    <i class="i-lucide-tag mr-1" />
                    {{ getCategoryName(templateForm.category) }}
                  </span>
                  <span
                    v-if="templateForm.language"
                    class="inline-flex items-center"
                  >
                    <i class="i-lucide-globe mr-1" />
                    {{ getLanguageName(templateForm.language) }}
                  </span>
                </div>
              </div>

              <!-- WhatsApp Preview Component -->
              <div v-if="canPreview">
                <WhatsAppPreview
                  :message="formatPreviewMessage"
                  :buttons="formatPreviewButtons"
                />
              </div>

              <!-- Empty State -->
              <div v-else class="text-center py-8 text-n-slate-11">
                <i class="i-lucide-message-circle text-3xl mb-2 block" />
                <p class="text-sm">
                  Digite o corpo da mensagem para ver a prévia
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.template-creator-container {
  position: relative;
  background-color: var(--bg);
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: overlay;
  height: 100%;
}

:global(.dark) .template-creator-container {
  background-color: #0f172a;
  color: #e2e8f0;
}

/* Enhanced form styling */
.template-creator-container input:focus,
.template-creator-container textarea:focus,
.template-creator-container select:focus {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Button hover effects */
.template-creator-container button {
  transition: all 0.2s ease;
}

.template-creator-container button:hover {
  transform: translateY(-1px);
}

/* Category and language selection styling */
.template-creator-container .grid button {
  position: relative;
  overflow: hidden;
}

.template-creator-container .grid button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
  transition: left 0.5s;
}

.template-creator-container .grid button:hover::before {
  left: 100%;
}

/* Shake animation for invalid input feedback */
@keyframes shake {
  0%,
  20%,
  40%,
  60%,
  80%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-5px);
  }
}

/* Enhanced input styling */
.template-creator-container input[type='text'] {
  transition:
    all 0.2s ease,
    box-shadow 0.15s ease;
}

.template-creator-container input[type='text']:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Variable highlighting in textarea */
.template-creator-container textarea {
  position: relative;
}

/* Variable highlighting overlay */
.variable-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow: hidden;
  background: transparent;
  color: transparent;
  text-shadow: none;
  box-shadow: none;
  outline: none;
}

/* Clean variable styling without shadows or outlines */
.variable-overlay span {
  text-shadow: none !important;
  box-shadow: none !important;
  outline: none !important;
  border: none !important;
  background: transparent !important;
}
</style>
